import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";

/** Moves focus to a screen's heading when it appears, so a screen reader announces the new screen. */
export function useFocusHeading<T extends HTMLElement = HTMLHeadingElement>(key?: unknown): RefObject<T | null> {
  const ref = useRef<T | null>(null);
  // A layout effect: focus lands in the same commit as the new screen, before anything paints or reads it.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Don't steal focus from a field the person is already in (e.g. after a re-render).
    const active = document.activeElement;
    if (active && active !== document.body && active.closest("input, textarea, select, [role='dialog']")) return;
    el.focus({ preventScroll: true });
  }, [key]);
  return ref;
}

/** Calls `fn` once, after mount, and not again (StrictMode-safe). */
export function useOnce(fn: () => void): void {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    fn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
